import { useState, useEffect, useRef } from "react";
import { 
  Search, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  TrendingUp, 
  Download,
  Calendar,
  X,
  User,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  Loader2,
  Filter
} from "lucide-react";
import api from "../../connector/api";
import EmployeeListLayout from "../components/layout/EmployeeLayout";

// Custom UI Select Dropdown Component
function CustomSelect({ options, value, onChange, placeholder, icon: Icon, isLoading }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => String(opt.value) === String(value));

  return (
    <div className="relative w-full" ref={containerRef}>
      <button
        type="button"
        disabled={isLoading}
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full bg-white border border-slate-200 focus:border-[#002855] focus:ring-4 focus:ring-[#002855]/5 rounded-xl px-3.5 py-2.5 text-xs outline-none shadow-3xs transition flex items-center justify-between cursor-pointer disabled:opacity-60"
      >
        <div className="flex items-center gap-2 truncate">
          {Icon && <Icon size={14} className="text-slate-400 shrink-0" />}
          <span className={selectedOption ? "text-slate-900 font-bold" : "text-slate-400 font-normal"}>
            {isLoading ? "Loading terms..." : selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          className={`text-slate-400 transition-transform duration-200 shrink-0 ${
            isOpen ? "rotate-180 text-[#002855]" : ""
          }`}
        />
      </button>

      {isOpen && !isLoading && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-slate-200/80 rounded-xl shadow-xl max-h-56 overflow-y-auto z-50 divide-y divide-slate-100">
          {options.map((option) => {
            const isSelected = String(option.value) === String(value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3.5 py-2.5 hover:bg-slate-50 flex items-center justify-between transition text-xs font-medium ${
                  isSelected ? "bg-slate-50 font-bold text-[#002855]" : "text-slate-700"
                }`}
              >
                <span>{option.label}</span>
                {isSelected && <Check size={14} className="text-[#002855] shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AttendanceReports() {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // Selected Employee Details
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeAttendance, setEmployeeAttendance] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Filter States
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedTermYear, setSelectedTermYear] = useState("all");
  const [termYearOptions, setTermYearOptions] = useState([{ label: "All Terms / Years", value: "all" }]);
  const [loadingTerms, setLoadingTerms] = useState(false);

  // Summary Stats
  const [employeeSummary, setEmployeeSummary] = useState({
    compliance: 0,
    present: 0,
    absent: 0,
    late: 0,
    total: 0
  });

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 10;

  // Static Month Filter Options (Numeric Month Values 1-12)
  const monthOptions = [
    { label: "All Months", value: "all" },
    { label: "January", value: "1" },
    { label: "February", value: "2" },
    { label: "March", value: "3" },
    { label: "April", value: "4" },
    { label: "May", value: "5" },
    { label: "June", value: "6" },
    { label: "July", value: "7" },
    { label: "August", value: "8" },
    { label: "September", value: "9" },
    { label: "October", value: "10" },
    { label: "November", value: "11" },
    { label: "December", value: "12" }
  ];

  // Fetch School Year Terms from router viewset endpoint
  useEffect(() => {
    const fetchTermYears = async () => {
      setLoadingTerms(true);
      try {
        const response = await api.get("year-terms/", { withCredentials: true });
        const results = response.data.results || response.data || [];

        const formattedTerms = results.map((item) => ({
          label: `${item.school_year_name} - ${item.term_name}${item.is_current ? " (Current)" : ""}`,
          value: String(item.id)
        }));

        setTermYearOptions([
          { label: "All Terms / Years", value: "all" },
          ...formattedTerms
        ]);
      } catch (error) {
        console.error("Failed to fetch term years list:", error);
      } finally {
        setLoadingTerms(false);
      }
    };

    fetchTermYears();
  }, []);

  // Debounced Employee Search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    const delayDebounce = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await api.get(
          `attendance/report/?search=${encodeURIComponent(searchQuery)}&page=1&page_size=8`,
          { withCredentials: true }
        );
        setSearchResults(response.data.results || []);
        setShowDropdown(true);
      } catch (error) {
        console.error("Failed to search employees:", error);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  // Fetch individual attendance when employee, filters, or page changes
  useEffect(() => {
    if (!selectedEmployee) return;

    const fetchIndividualAttendance = async () => {
      setLoadingAttendance(true);
      try {
        const queryParams = new URLSearchParams({
          employee_id: selectedEmployee.id,
          page: currentPage,
          page_size: pageSize
        });

        if (selectedMonth !== "all") queryParams.append("month", selectedMonth);
        if (selectedTermYear !== "all") queryParams.append("term_year", selectedTermYear);

        const response = await api.get(
          `attendance/history/?${queryParams.toString()}`,
          { withCredentials: true }
        );

        setEmployeeAttendance(response.data.results || []);
        setTotalPages(response.data.total_pages || 1);
        setTotalRecords(response.data.total_records || 0);

        if (response.data.summary) {
          setEmployeeSummary(response.data.summary);
        }
      } catch (error) {
        console.error("Failed to fetch employee attendance logs:", error);
      } finally {
        setLoadingAttendance(false);
      }
    };

    fetchIndividualAttendance();
  }, [selectedEmployee, currentPage, selectedMonth, selectedTermYear]);

  // Close search dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.employee-search-container')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleSelectEmployee = (emp) => {
    setSelectedEmployee(emp);
    setSearchQuery("");
    setShowDropdown(false);
    setCurrentPage(1);
  };

  const handleClearSelection = () => {
    setSelectedEmployee(null);
    setEmployeeAttendance([]);
    setSelectedMonth("all");
    setSelectedTermYear("all");
    setEmployeeSummary({ compliance: 0, present: 0, absent: 0, late: 0, total: 0 });
  };

  // Export CSV for Selected Employee with Active Filters
  const downloadCSV = async () => {
    if (!selectedEmployee) return;

    setIsExporting(true);
    try {
      const queryParams = new URLSearchParams({
        employee_id: selectedEmployee.id,
        export: "true"
      });

      if (selectedMonth !== "all") queryParams.append("month", selectedMonth);
      if (selectedTermYear !== "all") queryParams.append("term_year", selectedTermYear);

      const response = await api.get(
        `attendance/history/?${queryParams.toString()}`,
        { withCredentials: true }
      );

      const allRecords = response.data.results || [];

      if (allRecords.length === 0) {
        alert("No attendance records available to export for the active filters.");
        return;
      }

      const headers = ["Date", "Status", "Comment", "Recorded By"];

      const rows = allRecords.map((log) => {
        let recordedByName = "-";
        if (log.recorded_by) {
          if (typeof log.recorded_by === "object") {
            recordedByName = `${log.recorded_by.first_name || ""} ${log.recorded_by.last_name || ""}`.trim() || log.recorded_by.username;
          } else {
            recordedByName = log.recorded_by;
          }
        }

        return [
          `"${log.date || "-"}"`,
          `"${log.status || "-"}"`,
          `"${log.comment || "-"}"`,
          `"${recordedByName}"`
        ];
      });

      const csvContent = [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      const empName = (selectedEmployee.name || "Employee").replace(/\s+/g, '_');
      link.setAttribute("href", url);
      link.setAttribute("download", `Attendance_${empName}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to export CSV:", error);
      alert("An error occurred while exporting attendance history.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <EmployeeListLayout>
      <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col pb-12 antialiased font-dynamic-title">
        <main className="flex-1 max-w-6xl w-full mx-auto px-4 pt-6 space-y-6">
          
          {/* Page Identity Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <span className="text-[8px] font-bold text-[#002855] uppercase tracking-wider bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200/40">
                Core HR Administration
              </span>
              <h1 className="text-sm font-semibold text-slate-900 tracking-tight mt-1.5">Employee Attendance Ledger</h1>
              <p className="text-[11px] text-slate-500 mt-0.5">Search and inspect individual employee attendance and compliance logs.</p>
            </div>
            
            {selectedEmployee && (
              <button
                type="button"
                onClick={downloadCSV}
                disabled={isExporting}
                className="self-start font-mono sm:self-auto h-9 px-3.5 bg-[#002855] hover:bg-[#001d3d] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-bold text-[11px] transition flex items-center gap-2 shadow-sm cursor-pointer"
              >
                {isExporting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    <span>Exporting...</span>
                  </>
                ) : (
                  <>
                    <Download size={14} />
                    <span>Export CSV</span>
                  </>
                )}
              </button>
            )}
          </div>

          {/* Controls Bar: Employee Search & Custom Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Custom Search Autocomplete Input */}
            <div className="relative employee-search-container z-20">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                Select Employee
              </label>

              <div className="relative">
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 flex items-center pointer-events-none text-slate-400">
                  {selectedEmployee && !showDropdown ? (
                    <User size={16} className="text-[#002855]" />
                  ) : (
                    <Search size={16} />
                  )}
                </div>

                <input
                  type="text"
                  placeholder={selectedEmployee ? selectedEmployee.name : "Select or search employee..."}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (!showDropdown) setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  className="w-full bg-white border border-slate-200 focus:border-[#002855] focus:ring-4 focus:ring-[#002855]/5 rounded-xl pl-10 pr-10 py-2.5 text-xs outline-none shadow-3xs transition cursor-pointer"
                />

                <div className="absolute right-3.5 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                  {isSearching ? (
                    <div className="w-3.5 h-3.5 border-2 border-[#002855] border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <ChevronDown
                      size={16}
                      className={`text-slate-400 transition-transform duration-200 ${
                        showDropdown ? "rotate-180 text-[#002855]" : ""
                      }`}
                    />
                  )}
                </div>
              </div>

              {/* Custom Search Dropdown */}
              {showDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-slate-200/80 rounded-xl shadow-xl max-h-64 overflow-y-auto z-50 divide-y divide-slate-100">
                  {searchResults.length > 0 ? (
                    searchResults.map((emp) => {
                      const isSelected = selectedEmployee?.id === emp.id;
                      return (
                        <button
                          key={emp.id}
                          type="button"
                          onClick={() => handleSelectEmployee(emp)}
                          className={`w-full text-left px-3.5 py-2.5 hover:bg-slate-50 flex items-center justify-between transition group ${
                            isSelected ? "bg-slate-50/80" : ""
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-lg font-black flex items-center justify-center text-xs transition ${
                                isSelected
                                  ? "bg-[#002855] text-white shadow-xs"
                                  : "bg-slate-100 text-[#002855] group-hover:bg-[#002855] group-hover:text-white"
                              }`}
                            >
                              {emp.name?.charAt(0)}
                            </div>
                            <div>
                              <p className="font-bold text-slate-900 text-xs group-hover:text-[#002855]">
                                {emp.name}
                              </p>
                              <p className="text-[10px] font-mono text-slate-400">
                                ID: {emp.position?.id_number || emp.id}
                              </p>
                            </div>
                          </div>

                          {isSelected && (
                            <Check size={16} className="text-[#002855] shrink-0" />
                          )}
                        </button>
                      );
                    })
                  ) : (
                    <div className="p-4 text-center text-xs text-slate-400 font-medium">
                      {isSearching
                        ? "Searching profiles..."
                        : "No matching employee records found."}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Custom Month Filter */}
            <div className="z-10">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                Filter By Month
              </label>
              <CustomSelect
                options={monthOptions}
                value={selectedMonth}
                onChange={(val) => {
                  setSelectedMonth(val);
                  setCurrentPage(1);
                }}
                placeholder="Select Month"
                icon={Calendar}
              />
            </div>

            {/* Custom Term Year Filter */}
            <div className="z-10">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                Filter By Term / Academic Year
              </label>
              <CustomSelect
                options={termYearOptions}
                value={selectedTermYear}
                onChange={(val) => {
                  setSelectedTermYear(val);
                  setCurrentPage(1);
                }}
                placeholder="Select Term / Year"
                icon={Filter}
                isLoading={loadingTerms}
              />
            </div>
          </div>

          {/* Selected Employee Info Banner & Metrics Grid */}
          {selectedEmployee ? (
            <div className="space-y-4">
              {/* Active Selected Card Header */}
              <div className="bg-white p-3 border border-slate-200/80 rounded-lg shadow-2xs flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#002855] text-white font-black flex items-center justify-center text-sm shadow-2xs">
                    {selectedEmployee.name?.charAt(0)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xs font-extrabold text-slate-900 tracking-tight">{selectedEmployee.name}</h2>
                      <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200/60 px-2 py-0.5 rounded-md">
                        Active
                      </span>
                    </div>
                    <p className="text-[11px] font-mono text-slate-400">
                      ID: {selectedEmployee.position?.id_number || selectedEmployee.id}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleClearSelection}
                  className="h-8 px-2.5 text-[11px] font-mono font-bold text-slate-500 hover:text-slate-800 bg-slate-100 hover:bg-slate-200/60 rounded-lg transition flex items-center gap-1.5"
                >
                  <X size={14} />
                  <span>Change Employee</span>
                </button>
              </div>

              {/* Individual Metrics Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="bg-white p-4 border border-slate-200/60 rounded-lg shadow-2xs flex flex-col justify-between">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider block">Compliance Score</span>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="text-sm font-black text-[#002855] tracking-tight">{employeeSummary.compliance}%</span>
                    <span className="text-[9px] text-emerald-600 font-bold flex items-center gap-0.5">
                      <TrendingUp size={10} /> Optimal
                    </span>
                  </div>
                </div>

                <div className="bg-white p-4 border border-slate-200/60 rounded-lg shadow-2xs flex flex-col justify-between">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Days Present</span>
                  <div className="flex items-center gap-2 mt-2 text-emerald-600">
                    <CheckCircle2 size={14} />
                    <span className="text-sm font-black text-slate-800 tracking-tight">{employeeSummary.present}</span>
                  </div>
                </div>

                <div className="bg-white p-4 border border-slate-200/60 rounded-lg shadow-2xs flex flex-col justify-between">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Days Absent</span>
                  <div className="flex items-center gap-2 mt-2 text-rose-500">
                    <XCircle size={14} />
                    <span className="text-sm font-black text-slate-800 tracking-tight">{employeeSummary.absent}</span>
                  </div>
                </div>

                <div className="bg-white p-4 border border-slate-200/60 rounded-lg shadow-2xs flex flex-col justify-between">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Late Slips</span>
                  <div className="flex items-center gap-2 mt-2 text-amber-500">
                    <Clock size={14} />
                    <span className="text-sm font-black text-slate-800 tracking-tight">{employeeSummary.late}</span>
                  </div>
                </div>
              </div>

              {/* Attendance Logs Table */}
              <div className="bg-white rounded-lg border border-slate-200/80 shadow-xs overflow-hidden">
                <div className="p-3.5 bg-slate-50/50 border-b border-slate-200/80 flex items-center gap-2">
                  <Calendar size={14} className="text-[#002855]" />
                  <h3 className="text-xs font-bold text-slate-800">Attendance Log History</h3>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                        <th className="p-3.5">Date</th>
                        <th className="p-3.5 text-center">Status</th>
                        <th className="p-3.5 text-center">Comment</th>
                        <th className="p-3.5 text-center">Recorded By</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
                      {loadingAttendance ? (
                        <tr>
                          <td colSpan={4} className="py-12 text-center text-slate-400 animate-pulse font-bold">
                            Fetching attendance history...
                          </td>
                        </tr>
                      ) : employeeAttendance.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="py-12 text-center text-slate-400 font-bold">
                            No attendance logs recorded matching selected filters.
                          </td>
                        </tr>
                      ) : (
                        employeeAttendance.map((log, index) => (
                          <tr key={log.id || index} className="hover:bg-slate-50/50 transition">
                            <td className="p-3.5 font-mono text-xs font-bold text-slate-900">
                              {log.date}
                            </td>
                            <td className="p-3.5 text-center">
                              <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${
                                log.status?.toLowerCase() === "present"
                                  ? "bg-emerald-50 text-emerald-600 border border-emerald-200/60"
                                  : log.status?.toLowerCase() === "absent"
                                  ? "bg-rose-50 text-rose-600 border border-rose-200/60"
                                  : "bg-amber-50 text-amber-600 border border-amber-200/60"
                              }`}>
                                {log.status || "Present"}
                              </span>
                            </td>
                            <td className="p-3.5 text-center font-mono text-slate-600">
                              {log.comment || "--"}
                            </td>
                            <td className="p-3.5 text-center font-mono text-slate-600">
                              {log.recorded_by ? (
                                typeof log.recorded_by === "object" ? (
                                  `${log.recorded_by.last_name || ""}, ${log.recorded_by.first_name || ""}`.trim() || log.recorded_by.username
                                ) : (
                                  log.recorded_by
                                )
                              ) : (
                                "--"
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                <div className="p-3.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-xs select-none">
                  <span className="text-slate-400 font-medium">
                    Showing <b className="text-slate-700 font-bold">{employeeAttendance.length}</b> of{" "}
                    <b className="text-slate-700 font-bold">{totalRecords}</b> logs
                  </span>

                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      disabled={currentPage === 1 || loadingAttendance}
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition disabled:opacity-40 disabled:hover:bg-white cursor-pointer"
                    >
                      <ChevronLeft size={14} />
                    </button>

                    <span className="text-slate-500 font-bold px-2">
                      Page {currentPage} of {totalPages}
                    </span>

                    <button
                      type="button"
                      disabled={currentPage === totalPages || loadingAttendance}
                      onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition disabled:opacity-40 disabled:hover:bg-white cursor-pointer"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Empty State Prompt */
            <div className="bg-white rounded-xl border border-slate-200/80 p-12 text-center space-y-3 shadow-2xs">
              <div className="w-12 h-12 bg-slate-100 text-[#002855] rounded-full flex items-center justify-center mx-auto">
                <User size={22} />
              </div>
              <h3 className="text-sm font-bold text-slate-900">No Employee Selected</h3>
              <p className="text-xs text-slate-500 max-w-sm mx-auto">
                Search and select an employee above to view their detailed attendance report ledger and apply custom filters.
              </p>
            </div>
          )}
        </main>
      </div>
    </EmployeeListLayout>
  );
}